import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Scissors, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, error, setError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/app/dashboard');
    } catch (err) {
      // Error is handled by auth context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--bg)' }}>
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative overflow-hidden items-center justify-center"
        style={{ backgroundColor: 'var(--brand-navy)' }}>
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0" 
          style={{ background: 'linear-gradient(135deg, rgba(197,165,90,0.14) 0%, rgba(27,42,74,0.06) 55%, rgba(251,250,247,0) 100%)' }} 
        />
        {/* Gold vertical rule */}
        <div className="absolute right-0 top-[15%] bottom-[15%] w-px" 
          style={{ backgroundColor: 'rgba(197,165,90,0.35)' }} 
        />
        <div className="relative z-10 max-w-md px-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" 
              style={{ backgroundColor: 'rgba(197,165,90,0.2)', border: '1px solid rgba(197,165,90,0.3)' }}>
              <Scissors className="w-6 h-6" style={{ color: 'var(--brand-gold)' }} />
            </div>
          </div>
          <h1 className="text-4xl xl:text-5xl font-semibold text-white mb-4" style={{ fontFamily: '"Bodoni Moda", Georgia, serif' }}>
            Herringbone<br />&amp; Sui
          </h1>
          <p className="text-base text-white/60 leading-relaxed" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            Bespoke menswear tailoring operations,<br />crafted with precision.
          </p>
          <div className="mt-12 flex items-center justify-center gap-6">
            <div className="h-px w-12" style={{ backgroundColor: 'rgba(197,165,90,0.3)' }} />
            <span className="text-xs uppercase tracking-[0.2em] text-white/40" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Mumbai</span>
            <div className="h-px w-12" style={{ backgroundColor: 'rgba(197,165,90,0.3)' }} />
          </div>
        </div>
      </div>

      {/* Right Auth Panel */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12">
        <div className="w-full max-w-[420px]">
          {/* Mobile brand header */}
          <div className="lg:hidden text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" 
                style={{ backgroundColor: 'rgba(27,42,74,0.08)', border: '1px solid rgba(27,42,74,0.12)' }}>
                <Scissors className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
              </div>
            </div>
            <h1 className="text-2xl font-semibold" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
              Herringbone &amp; Sui
            </h1>
          </div>

          <Card className="border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)] rounded-xl">
            <CardHeader className="pb-2 pt-8 px-8">
              <h2 className="text-2xl font-semibold" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
                Sign In
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
                Access your operations dashboard
              </p>
            </CardHeader>
            <CardContent className="px-8 pb-8 pt-4">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Email</Label>
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="you@herringboneandsui.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    className="h-11 rounded-lg bg-white border-border/80 focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:ring-offset-2"
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(null); }}
                      className="h-11 rounded-lg bg-white border-border/80 pr-11 focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:ring-offset-2"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div data-testid="login-error-text" className="text-sm py-2.5 px-3.5 rounded-lg" 
                    style={{ color: 'var(--danger)', backgroundColor: 'rgba(180,35,24,0.06)', border: '1px solid rgba(180,35,24,0.12)' }}>
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={isLoading}
                  className="w-full h-11 rounded-xl text-sm font-medium hover:translate-y-[-1px] active:translate-y-0 active:scale-[0.99]"
                  style={{ 
                    backgroundColor: 'var(--brand-navy)', 
                    color: 'var(--bg)',
                    transition: 'transform 150ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 150ms cubic-bezier(0.2,0.8,0.2,1)'
                  }}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs mt-6" style={{ color: 'var(--ink-muted)' }}>
            SuiOS v1.0 — Bespoke Tailoring Operating System
          </p>
        </div>
      </div>
    </div>
  );
}
